import { GESTIFY_HOSTS, type GestifyEnvironment } from '@k-management/shared';
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';

/**
 * Gestify from the browser (decision of 05/10/2026, ADR-017). The firewall of
 * Gestify refuses the calls of our server on Google Cloud, but it lets the
 * browsers of K-Rev through and its API accepts calls from any site. So the
 * browser signs in to Gestify with the user's own account, reads, and hands
 * the data to K-Management, which checks it as an imported file.
 *
 * The password goes straight from this page to Gestify and is not kept; the
 * tokens live in the memory of the tab only (never in storage, never sent to
 * our server) and die with it. Only reads: nothing is written in Gestify.
 */

export interface GestifyTarget {
  environment: GestifyEnvironment;
  /** The company to read when the user belongs to more than one; null for the default. */
  companyId: number | null;
}

interface Session {
  username: string;
  access: string;
  refresh: string | null;
  /** Milliseconds: the `exp` of the access token, or a few minutes when it has none. */
  expiresAt: number;
  hosts: { auth: string; data: string };
}

export type GestifyConnectResult = { status: 'connected' } | { status: 'code'; token: string };

/** A failure already said in Italian. */
export class GestifyBrowserError extends Error {}

interface GestifyBrowser {
  /** The user signed in, or null. */
  connectedAs: string | null;
  connect(username: string, password: string, target: GestifyTarget): Promise<GestifyConnectResult>;
  /** Second step when Gestify asks for the code of the authenticator app. */
  verify(token: string, code: string, target: GestifyTarget, username: string): Promise<void>;
  /** Every page of a list of the data API, e.g. `/api/anagrafica/v1/list`. */
  readAll(
    path: string,
    onProgress?: (read: number, total: number | null) => void,
  ): Promise<Record<string, unknown>[]>;
  /** The total of a list, reading one record. */
  count(path: string): Promise<number | null>;
  /** One record of the data API, e.g. the detail of a document. */
  read(path: string): Promise<Record<string, unknown>>;
  /** A file of the data API as it comes (the XML of an invoice), with its type. */
  download(path: string): Promise<{ contentType: string; bytes: Uint8Array }>;
  disconnect(): void;
}

const Context = createContext<GestifyBrowser | null>(null);

const PAGE = 100;
const MAX_PAGES = 200;

export function GestifyBrowserProvider({ children }: { children: ReactNode }) {
  const [connectedAs, setConnectedAs] = useState<string | null>(null);
  // A ref, not state: the tokens never reach React DevTools or a re-render.
  const session = useRef<Session | null>(null);

  const start = useCallback(
    async (tokens: Record<string, unknown>, target: GestifyTarget, username: string) => {
      const hosts = GESTIFY_HOSTS[target.environment];
      let current = sessionOf(tokens, username, hosts);
      if (target.companyId !== null) {
        const changed = await call(`${hosts.auth}/api/user/v1/session/change`, {
          method: 'POST',
          token: current.access,
          body: { azienda_id: target.companyId },
        });
        current = sessionOf(changed, username, hosts);
      }
      session.current = current;
      setConnectedAs(username);
    },
    [],
  );

  const connect = useCallback(
    async (username: string, password: string, target: GestifyTarget) => {
      const hosts = GESTIFY_HOSTS[target.environment];
      const answer = await call(`${hosts.auth}/api/user/v1/login`, {
        method: 'POST',
        body: { username, password },
      });
      if (answer.mfa_required) {
        const token = typeof answer.mfa_login_token === 'string' ? answer.mfa_login_token : '';
        if (!token) throw new GestifyBrowserError('Gestify chiede il codice ma non dice come.');
        return { status: 'code', token } as const;
      }
      await start(answer, target, username);
      return { status: 'connected' } as const;
    },
    [start],
  );

  const verify = useCallback(
    async (token: string, code: string, target: GestifyTarget, username: string) => {
      const hosts = GESTIFY_HOSTS[target.environment];
      const answer = await call(`${hosts.auth}/api/user/v1/mfa/verify`, {
        method: 'POST',
        body: { mfa_login_token: token, mfa_code: code.trim() },
      });
      await start(answer, target, username);
    },
    [start],
  );

  const disconnect = useCallback(() => {
    session.current = null;
    setConnectedAs(null);
  }, []);

  /** A valid access token: renewed shortly before it expires. */
  const access = useCallback(async (): Promise<Session> => {
    const current = session.current;
    if (!current) throw new GestifyBrowserError('Collegati a Gestify prima di leggere i dati.');
    if (Date.now() < current.expiresAt - 60_000) return current;
    if (!current.refresh) {
      disconnect();
      throw new GestifyBrowserError('La sessione di Gestify è scaduta: ricollegati.');
    }
    try {
      const renewed = await call(
        `${current.hosts.auth}/api/user/v1/refresh/${encodeURIComponent(current.refresh)}`,
        { method: 'GET' },
      );
      session.current = sessionOf(renewed, current.username, current.hosts);
      return session.current;
    } catch {
      disconnect();
      throw new GestifyBrowserError('La sessione di Gestify è scaduta: ricollegati.');
    }
  }, [disconnect]);

  const page = useCallback(
    async (path: string, skip: number, limit: number) => {
      const current = await access();
      const url = new URL(`${current.hosts.data}${path}`);
      url.searchParams.set('skip', String(skip));
      url.searchParams.set('limit', String(limit));
      const answer = await call(url.toString(), { method: 'GET', token: current.access });
      const data = Array.isArray(answer.data) ? (answer.data as Record<string, unknown>[]) : [];
      const info = answer.info as { total_count?: unknown } | undefined;
      const total = typeof info?.total_count === 'number' ? info.total_count : null;
      return { data, total };
    },
    [access],
  );

  const readAll = useCallback(
    async (path: string, onProgress?: (read: number, total: number | null) => void) => {
      const all: Record<string, unknown>[] = [];
      for (let index = 0; index < MAX_PAGES; index += 1) {
        const { data, total } = await page(path, index * PAGE, PAGE);
        all.push(...data);
        onProgress?.(all.length, total);
        if (data.length < PAGE || (total !== null && all.length >= total)) return all;
      }
      throw new GestifyBrowserError('Troppi dati da leggere in una volta (oltre 20.000).');
    },
    [page],
  );

  const count = useCallback(async (path: string) => (await page(path, 0, 1)).total, [page]);

  const download = useCallback(
    async (path: string) => {
      const current = await access();
      let response: Response;
      try {
        response = await fetch(`${current.hosts.data}${path}`, {
          headers: { authorization: `Bearer ${current.access}` },
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
        });
      } catch {
        throw new GestifyBrowserError('Gestify non risponde da questa rete.');
      }
      if (!response.ok) {
        throw new GestifyBrowserError(`Gestify ha risposto con un errore (${response.status}).`);
      }
      return {
        contentType: response.headers.get('content-type') ?? '',
        bytes: new Uint8Array(await response.arrayBuffer()),
      };
    },
    [access],
  );

  const read = useCallback(
    async (path: string) => {
      const current = await access();
      const answer = await call(`${current.hosts.data}${path}`, {
        method: 'GET',
        token: current.access,
      });
      // The record, alone or inside `data`.
      const data = answer.data;
      return data !== null && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : answer;
    },
    [access],
  );

  return (
    <Context.Provider
      value={{ connectedAs, connect, verify, readAll, count, read, download, disconnect }}
    >
      {children}
    </Context.Provider>
  );
}

export function useGestifyBrowser(): GestifyBrowser {
  const value = useContext(Context);
  if (!value) throw new Error('GestifyBrowserProvider is missing');
  return value;
}

function sessionOf(
  tokens: Record<string, unknown>,
  username: string,
  hosts: { auth: string; data: string },
): Session {
  const access = typeof tokens.access_token === 'string' ? tokens.access_token : '';
  if (!access) throw new GestifyBrowserError('Gestify non ha dato l’accesso: riprova.');
  const refresh = typeof tokens.refresh === 'string' ? tokens.refresh : null;
  return { username, access, refresh, expiresAt: expiryOf(access), hosts };
}

/** The `exp` of a JWT, in milliseconds; five minutes from now when it cannot be read. */
function expiryOf(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp === 'number') return payload.exp * 1000;
  } catch {
    // Not a JWT we can read.
  }
  return Date.now() + 5 * 60_000;
}

async function call(
  url: string,
  init: { method: 'GET' | 'POST'; token?: string; body?: unknown },
): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: init.method,
      headers: {
        accept: 'application/json',
        ...(init.token ? { authorization: `Bearer ${init.token}` } : {}),
        ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      // Nothing of K-Management goes to Gestify.
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    });
  } catch {
    throw new GestifyBrowserError(
      'Gestify non risponde da questa rete. Prova dalla rete dell’ufficio o di casa, senza VPN.',
    );
  }
  const answer = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok) {
    const reason = [answer?.error, answer?.message, answer?.detail].find(
      (item): item is string => typeof item === 'string' && item.trim() !== '',
    );
    if (response.status === 401) {
      throw new GestifyBrowserError(`Gestify non accetta l’accesso${reason ? `: ${reason}` : ''}.`);
    }
    if (response.status === 403 && !answer) {
      throw new GestifyBrowserError(
        'Il firewall di Gestify blocca questa rete. Prova dalla rete dell’ufficio o di casa, senza VPN.',
      );
    }
    throw new GestifyBrowserError(
      `Gestify ha risposto con un errore (${response.status}${reason ? ` – ${reason}` : ''}).`,
    );
  }
  return answer ?? {};
}
